import { InjectionToken } from '@angular/core';
import { ContractListItem } from '@app/features/contracts/contract.model';
import { Observable } from 'rxjs';

/**
 * Origine d'une entrée du fil d'historique : ce qui l'a produite (validation, contrat,
 * revalorisation…). Le fil se filtre par origine, et la clé sert de repère au style de la pastille.
 */
export interface HistoryOrigin {
  readonly key: string;
  readonly label: string;
  /** Précision affichée sous le fil dès qu'une entrée de cette origine y figure. */
  readonly note?: string;
}

/**
 * Fragment de texte d'une entrée. Un utilisateur est désigné par son identifiant et nommé à
 * l'affichage, ce qui garde les sources indépendantes de l'annuaire.
 */
export type HistoryTextPart = string | { readonly userId: string | null };
export type HistoryText = readonly HistoryTextPart[];

export type HistoryTone = 'success' | 'danger' | 'muted';

/** Ligne secondaire d'une entrée, sous son résumé. */
export interface HistoryLine {
  readonly text: HistoryText;
  readonly tone?: HistoryTone;
}

/** Ligne du détail qu'une entrée déplie : à qui, et ce qu'il en est advenu. */
export interface HistoryDetail {
  readonly label: HistoryText;
  readonly value: string;
  readonly tone?: HistoryTone;
}

export interface HistoryAction {
  readonly label: string;
  readonly run: () => void;
}

/**
 * Ce sur quoi porte une entrée quand ce n'est pas l'embauche : un avenant. Le fil le marque, et le
 * filtre de son origine retient l'entrée, quelle que soit la sienne.
 */
export interface HistorySubject {
  readonly label: string;
  readonly origin: HistoryOrigin;
}

export interface HistoryEntry {
  /** Unique dans le fil : il sert au suivi du rendu et à l'état déplié. */
  readonly id: string;
  readonly origin: HistoryOrigin;
  /** Absent pour ce qui porte sur l'embauche ou sur le contrat lui-même. */
  readonly subject?: HistorySubject;
  /** Date ISO de ce qui a eu lieu, ou de ce qui aura lieu pour une entrée à venir. */
  readonly at: string;
  /** Vrai pour ce qui arrivera si rien ne change : l'entrée se tient en tête, à part. */
  readonly upcoming?: boolean;
  readonly title: HistoryText;
  readonly lines?: readonly HistoryLine[];
  readonly comment?: string | null;
  readonly details?: readonly HistoryDetail[];
  readonly action?: HistoryAction;
}

/**
 * Producteur d'entrées pour une origine. Le fil réunit toutes les sources fournies sous
 * `CONTRACT_HISTORY_SOURCES` : une origine nouvelle s'ajoute par une source de plus.
 */
export interface ContractHistorySource {
  readonly origin: HistoryOrigin;
  entries(contract: ContractListItem): Observable<HistoryEntry[]>;
}

export const CONTRACT_HISTORY_SOURCES = new InjectionToken<ContractHistorySource[]>(
  'CONTRACT_HISTORY_SOURCES',
);
